import { Message, Participant } from '@twilio/conversations';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import { ApiError } from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import Messages from './Messages';
import Reconnect from './Reconnect';

import { useAppConfig } from 'hooks/appConfig';
import { useAuthentication } from 'hooks/authentication';
import { useChatContext } from 'hooks/chatContext';
import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useUrlListener } from 'hooks/useUrlListener';

import { startConversation } from 'services/chatBackend/startConversation';
import { putPropelMojoContext } from 'services/propel';
import { getTwilio } from 'services/twilio';

import { getLocalStorageItem, updateLocalStorage } from 'utils/browserCache';
import { getMojoContext } from 'utils/chatContext';
import { DataDogLogger } from 'utils/dataDog';
import { getInitLoggerObj } from './utils/initLogger';
import { htmlId } from 'utils/html';
import {
  checkMessageForInsecureUrls,
  formatMessages,
  scrollToMessagesListBottom,
  secureUserMessage,
} from 'utils/message';
import { formatParticipants } from 'utils/twilio';
import { getAndSetIsForcedSystemTypingAnimation, resyncMessagesIfNeeded } from './utils';

import { ANALYTICS } from 'analytics/constants';
import { InitLog } from './types';
import { AnalyticConversationStartedType, AnalyticTimeElapsedDetailsType } from 'analytics/types';
import { ParticipantActionType } from 'components/Avatar/Avatar.types';
import { LOCAL_STORAGE_KEY } from 'constants/browserCache';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import {
  CONVERSATION_MESSAGES_MAX,
  PARTICIPANT_PREFIX,
  TWILIO_CONNECTION_STATE,
} from 'constants/twilio';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { StyledMainContentWrapper } from './styles';

const Chat = (): ReactElement => {
  const { trackEvent } = useAnalytics();
  const { appConfigState } = useAppConfig();
  const { authenticationState, dispatchAuthenticationAction } = useAuthentication();
  const { chatContextState, dispatchChatContextAction } = useChatContext();
  const { conversationState, dispatchConversationAction } = useConversation();
  const { showBoundary } = useErrorBoundary();
  const { url } = useUrlListener(true);

  const mainContentRef = useRef<HTMLDivElement>(null);
  const resyncLastMsgIndex = useRef<number>(0);
  const hasLatestMsgAlreadyResynced = useRef<boolean>(false);
  const hasInit = useRef<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const [twilioEventProxy, setTwilioEventProxy] = useState<any>(undefined);
  const [isForcedSystemTypingAnimation, setIsForcedSystemTypingAnimation] = useState(false);
  const [lastForcedSysTypingAnimationTimestamp, setLastForcedSysTypingAnimationTimestamp] =
    useState<number | undefined>(undefined);

  // Make sure chatContext is synced. Some apps (like Purchase Page) may load after ChatUI and also update the URL
  // after. Which means chatContext upon ChatUI initialization may not reflect latest URL just a few seconds later.
  // If chat is closed, chatContext will auto sync next time it is opened again.
  useEffect(() => {
    const { urlCurrentAiContext } = chatContextState;
    if (urlCurrentAiContext && url !== urlCurrentAiContext) {
      const newContext = getMojoContext();
      window[CHATUI_WINDOW_PATH].putContext(newContext);
    }
  }, [url]);

  useEffect(() => {
    if (!isForcedSystemTypingAnimation && lastForcedSysTypingAnimationTimestamp) {
      const elapsed = Date.now() - lastForcedSysTypingAnimationTimestamp;
      DataDogLogger.action(CUSTOM_ACTION_NAMES.ENGINE_DELAYED_RESPONSE, {
        timeElapsed: elapsed,
        timeStart: lastForcedSysTypingAnimationTimestamp,
        timeEnd: Date.now(),
      });
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.TIME_ELAPSED,
        details: {
          analytic_time_id: 'engine_delayed_response',
          time_start: lastForcedSysTypingAnimationTimestamp,
          time_end: Date.now(),
          time_elapsed: elapsed,
        } as AnalyticTimeElapsedDetailsType,
      });
    }
  }, [isForcedSystemTypingAnimation]);

  useEffect(() => {
    if (twilioEventProxy && conversationState.twilio) {
      const { client, conversation } = conversationState.twilio;
      // client event ref: https://sdk.twilio.com/js/conversations/releases/2.1.0/docs/classes/Client.html
      // conversation event ref: https://sdk.twilio.com/js/conversations/releases/2.0.1/docs/classes/Conversation.html
      client.on('connectionStateChanged', (state) => {
        if (appConfigState.isDebugMode) {
          console.log('ChatUI debug - Twilio client connectionStateChanged:', state);
        }
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: { isConnected: state === TWILIO_CONNECTION_STATE.CONNECTED },
        });
      });
      client.on('connectionError', (err) => {
        if (appConfigState.isDebugMode) {
          console.log('ChatUI debug - Twilio client connectionError:', err);
        }
      });
      conversation.on('messageAdded', (newMessage) => {
        if (appConfigState.isDebugMode) {
          console.log('ChatUI debug - Twilio conversation messageAdded:', newMessage);
        }
        checkMessageForInsecureUrls(newMessage);
        onHandleNewMessage(newMessage);
      });
      conversation.on('participantJoined', (participant) => {
        if (appConfigState.isDebugMode) {
          console.log('ChatUI debug - Twilio conversation participantJoined:', participant);
        }
        onHandleParticipantChange(participant, 'joined');
      });
      conversation.on('participantLeft', (participant) => {
        if (appConfigState.isDebugMode) {
          console.log('ChatUI debug - Twilio conversation participantLeft:', participant);
        }
        onHandleParticipantChange(participant, 'left');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twilioEventProxy, conversationState.twilio]);

  /** For Scroll UX experiment, reload chat to where user last left off (if available) **/
  useEffect(() => {
    if (conversationState.isConnected && mainContentRef.current) {
      mainContentRef.current.style.opacity = '0'; // hide UI as content renders
      const prevSessionScrollPosition = getLocalStorageItem(
        LOCAL_STORAGE_KEY.CVNA_CHAT_UI_SCROLL_POS,
      );
      setTimeout(() => {
        if (mainContentRef.current) {
          if (typeof prevSessionScrollPosition === 'number') {
            mainContentRef.current.scrollTo({
              top: prevSessionScrollPosition,
              behavior: 'instant',
            });
          } else {
            scrollToMessagesListBottom();
          }
          mainContentRef.current.style.opacity = '1';
        }
      }, 500);
    }
    if (!conversationState.isConnected && conversationState.isInitializationRequested) {
      initializeConversation();
      window[CHATUI_WINDOW_PATH].startInactivityCounter();
    }
  }, [conversationState.isConnected, conversationState.isInitializationRequested]);

  /** Failsafe when user is routed to Chat screen but initialization has not occurred yet (blank screen) **/
  useEffect(() => {
    setTimeout(() => {
      if (!hasInit.current && !conversationState.isConnected) {
        DataDogLogger.action(CUSTOM_ACTION_NAMES.CHAT_INITIALIZE_BLANK_SCREEN);
        initializeConversation();
      }
    }, 1500);
  }, []);

  const trackInitSuccess = (initLog: InitLog) => {
    DataDogLogger.action(CUSTOM_ACTION_NAMES.CHAT_INITIALIZED, { chatInit: initLog });
    if (!conversationState.isInactivityTimeout) {
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.CONVERSATION_STARTED,
        details: {
          is_existing_convo: conversationState.isExistingConversation,
          is_valid_auth_cache: initLog.isCachedAuthValid,
          was_init_view_open: initLog.wasInitViewOpen,
          time_init_api: initLog.msElapsed.loadingTotal,
          time_init_convo: initLog.timestampCurrInit - authenticationState.unixTimeAppLoaded,
        } as AnalyticConversationStartedType,
      });
    }
  };

  const initializeConversation = async () => {
    hasInit.current = true;
    setIsLoading(true);

    // Capture init performance metrics. Properties progressively get set.
    const initLog = getInitLoggerObj(authenticationState, conversationState);

    try {
      if (twilioEventProxy) {
        // Create proxy for Twilio SDK to interact via event listeners instead of React hooks.
        // Also prevents duplicate instances and event listeners. Ref: https://www.twilio.com/docs/conversations/javascript/exploring-conversations-javascript-quickstart
        await twilioEventProxy.conversation.removeAllListeners();
        setTwilioEventProxy(null);
      }
      if (conversationState.twilio) {
        await conversationState.twilio.conversation.removeAllListeners();
      }

      let startConvo;
      let aiContext;
      let twilio;
      // isolate each API call into own try/catch block for logging purposes
      try {
        const stampStartConvo = Date.now();
        startConvo = await startConversation();
        initLog.msElapsed.startConversation = Date.now() - stampStartConvo;
        initLog.retries.startConversation = startConvo.retryCount;
        initLog.isCachedAuthValid = Boolean(startConvo.isCachedAuthValid);
        delete startConvo.isCachedAuthValid; // only used for analytics and tracking
      } catch (error) {
        const errorContext = { authenticationState, error, startConvo };
        const httpError = new ApiError('Initialization error - /conversation/start API failed.');
        showBoundary({ error: httpError, context: errorContext });
        return;
      }
      try {
        const stampPutContext = Date.now();
        const newAiContext = chatContextState.isInitWithDynamicHandRaiser
          ? chatContextState.mojoContext // set previously by dynamic handraiser
          : getMojoContext();
        aiContext = startConvo && (await putPropelMojoContext(newAiContext, startConvo));
        initLog.msElapsed.putContext = Date.now() - stampPutContext;
        initLog.retries.putContext = aiContext.retryCount;
      } catch (error) {
        // Precedes Twilio calls, but doesn't have to be successful to continue with initialization.
        const errorContext = {
          isCachedAuthValid: initLog.isCachedAuthValid,
          authenticationState,
          error,
          aiContext,
          startConvo,
        };
        const httpError = new ApiError('Initialization error - Mojo context setter API failed.');
        DataDogLogger.error(httpError, errorContext);
      }
      try {
        twilio = startConvo && (await getTwilio(startConvo));
        initLog.msElapsed.twilioInit = twilio.elapsedTwilioInit;
        initLog.retries.twilio = twilio.retryCount;
      } catch (e: any) {
        const errorContext = {
          ...e.errorContext,
          isCachedAuthValid: initLog.isCachedAuthValid,
          authenticationState,
          aiContext,
          startConvo,
        };
        showBoundary({ error: e, context: errorContext });
        return;
      }

      if (conversationState.questionWhenInitializing) {
        const stampQuestionSend = Date.now();
        try {
          // object data schema to support PopularTopics, QuickReplies, etc
          const msgBody =
            typeof conversationState.questionWhenInitializing === 'object'
              ? {
                  text: secureUserMessage(conversationState.questionWhenInitializing.message),
                  payload: conversationState.questionWhenInitializing.enginePayload,
                }
              : {
                  text: secureUserMessage(conversationState.questionWhenInitializing),
                };
          await twilio?.conversation.sendMessage(JSON.stringify(msgBody));
          initLog.msElapsed.questionSend = Date.now() - stampQuestionSend;

          // Don't await. Reduces time spinner shown.
          getAndSetIsForcedSystemTypingAnimation(
            true,
            true,
            setIsForcedSystemTypingAnimation,
            setLastForcedSysTypingAnimationTimestamp,
          );
          DataDogLogger.action(CUSTOM_ACTION_NAMES.USER_MESSAGE_SENT, {
            clientConnectionState: twilio?.client.connectionState,
            invocation: 'initialization',
          });
        } catch (e: any) {
          return showBoundary(e);
        }
      }

      // Timing of getMessages only has slight positive correlation to total messages length. getParticipants is almost instant.
      const stampGetMessages = Date.now();
      const messages = await twilio?.conversation.getMessages(CONVERSATION_MESSAGES_MAX);
      initLog.msElapsed.twilioGetMessages = Date.now() - stampGetMessages;
      initLog.conversationMessageCount = messages?.items?.length;
      const participants = await twilio?.conversation.getParticipants();

      if (!messages || !participants) {
        DataDogLogger.error(new Error('Unable to retrieve messages and/or participants'), {
          isCachedAuthValid: initLog.isCachedAuthValid,
          authenticationState,
          aiContext,
          startConvo,
          messages,
          participants,
        });
        return;
      }

      setTwilioEventProxy(twilio);
      dispatchAuthenticationAction({
        type: ACTIONS.CHANGE,
        payload: {
          propel: startConvo,
          unixTimeLastInitialization: Date.now(),
        },
      });
      dispatchChatContextAction({
        type: ACTIONS.CHANGE,
        payload: {
          isAiContextSent: true,
          isInitWithDynamicHandRaiser: false,
          mojoContext: aiContext,
          urlCurrentAiContext: window.location.href,
        },
      });
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: {
          twilio,
          isConnected: true,
          isExistingConversation: true,
          isInactivityTimeout: false,
          isInitializationRequested: false,
          messages: formatMessages(messages.items),
          participants: formatParticipants(participants),
          questionWhenInitializing: '',
          questionInputText: conversationState.questionWhenInitializing
            ? ''
            : conversationState.questionInputText,
        },
      });
      initLog.msElapsed.loadingTotal = Date.now() - initLog.timestampCurrInit;
      trackInitSuccess(initLog);
    } catch (e: any) {
      const err = new ApiError(
        `Critical issue encountered while initializing conversation. Info: ${String(e)}`,
      );
      showBoundary(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onHandleParticipantChange = async (
    participant: Participant,
    action: ParticipantActionType,
  ) => {
    // TODO - log analytic event
    if (conversationState.twilio) {
      const newParticipants = await conversationState.twilio.conversation.getParticipants();
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: { participants: formatParticipants(newParticipants) },
      });
    }
    if (appConfigState.isNativeMobileApp) {
      action === 'joined' && window?.webkit?.messageHandlers?.advocateJoined.postMessage('');
      action === 'left' && window?.webkit?.messageHandlers?.advocateLeft.postMessage('');
    }
  };

  const onHandleNewMessage = async (newMessage: Message) => {
    hasLatestMsgAlreadyResynced.current = false;
    const isCustomerMsg = newMessage.author?.includes(PARTICIPANT_PREFIX.CUSTOMER) || false;
    window[CHATUI_WINDOW_PATH].startInactivityCounter();
    await getAndSetIsForcedSystemTypingAnimation(
      isCustomerMsg,
      false,
      setIsForcedSystemTypingAnimation,
      setLastForcedSysTypingAnimationTimestamp,
    );
    const messages = await window[CHATUI_WINDOW_PATH].getMessages();
    resyncLastMsgIndex.current = messages.length - 1;
    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: { messages, isAnimateLastMessage: true, unixTimeLastMsgReceived: Date.now() },
    });

    if (isCustomerMsg) {
      await resyncMessagesIfNeeded({
        newMessage,
        dispatchConversationAction,
        hasLatestMsgAlreadyResynced,
        resyncLastMsgIndex,
      });
    }

    const existingCache = getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_CHAT_UI);
    updateLocalStorage(LOCAL_STORAGE_KEY.CVNA_CHAT_UI, {
      ...(existingCache && existingCache),
      conversationMsgCount: messages.length,
    });
  };

  const getMainContent = () => {
    const { isInactivityTimeout, twilio } = conversationState;
    if (isInactivityTimeout) {
      return <Reconnect />;
    }
    switch (twilio?.client.connectionState) {
      case TWILIO_CONNECTION_STATE.CONNECTED:
        return (
          <Messages
            mainContentRef={mainContentRef}
            isForcedSystemTypingAnimation={isForcedSystemTypingAnimation}
          />
        );
      case TWILIO_CONNECTION_STATE.DISCONNECTED:
      case TWILIO_CONNECTION_STATE.DENIED:
        return <Reconnect />;
      case TWILIO_CONNECTION_STATE.CONNECTING:
      default:
        return null;
    }
  };

  return (
    <StyledMainContentWrapper id={htmlId('chat-main-content')} ref={mainContentRef}>
      <Loader isLoading={isLoading}>{getMainContent()}</Loader>
    </StyledMainContentWrapper>
  );
};

export default Chat;
